/**
 * MC Analytics -- Chart rendering, server selector, heatmap, and session DataTable.
 *
 * Initializes on /mc/analytics page only (guards on #mc-analytics-chart presence).
 * Fetches chart data via AJAX from McPresenter::handleGetAnalyticsChart signal.
 * Renders Highcharts stacked area chart (all servers) or single area chart (one server).
 * Renders GitHub-style activity heatmap via McPresenter::handleGetHeatmap signal.
 * Initializes server-side DataTable for session history via McPresenter::handleGetSessions signal.
 */

import * as Highcharts from 'highcharts';
import flatpickr from 'flatpickr';
import DataTable from 'datatables.net-bs5';
import { init_tooltips } from '../util';
import { getChartTheme } from '../utils/chartTheme';
// Phase 240 (240-06, D-05): the single app-wide "absolute instant — relative age" header used by
// every in-scope chart tooltip. Dependency-free module (240-01) — importing it drags no
// bootstrap/DataTables/Coloris weight into this chunk.
import { chartInstantHeader } from '../utils/instants';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ChartSeries {
    name: string;
    points: [number, number][];
}

interface ChartPayload {
    series: ChartSeries[];
    stacked: boolean;
}

interface HeatmapDay {
    hours: number;
    players: string;
}

interface HeatmapPayload {
    days: Record<string, HeatmapDay>;
}

/** The identity portion of a getSessionsTable() row (Plan 256-06) -- declared explicitly rather
 *  than read through the generic Record<string, unknown> shape every other cell's row-wiring
 *  callback uses, so a typo in a field name is a tsc error rather than a silently-undefined href. Mirrors
 *  allStats.ts's AllStatsIdentityRow with ONE difference: `player_uuid` is nullable here (D-04) --
 *  a session row's `player_name` may have no `mc_players` match, a state all-stats never has
 *  because it is sourced from Lane B, which always carries a resolved uuid. */
interface SessionIdentityRow {
    player: string;
    player_uuid: string | null;
    server: string;
    server_key: string;
    server_icon: string | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SERVER_COLORS = [
    '#2196F3',
    '#4CAF50',
    '#FF9800',
    '#9C27B0',
    '#00BCD4',
    '#F44336',
    '#795548',
    '#607D8B',
    '#E91E63',
    '#3F51B5',
];

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

/**
 * D-09/D-10/D-11/D-12 Phase 249: the checked server keys, or `[]` — the D-10 "all servers" state.
 * Widened from a single scalar to an array (mirroring feed.ts's/leaderboard.ts's identical state
 * shape, Plan 249-08/this plan's own Task 2). Every request re-validates `server[]` server-side
 * through `readServerKeys()`; the client-side option-existence check in `initAnalytics()` below is
 * a UX affordance only.
 */
let currentServers: string[] = [];
let currentRange: string = '24h';
let chartInstance: Highcharts.Chart | null = null;
let customFrom: string | undefined;
let customTo: string | undefined;
let sessionsTable: any | null = null;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function initAnalytics(): void {
    const chartContainer = document.getElementById('mc-analytics-chart');
    if (!chartContainer) return; // Only runs on /mc/analytics page

    // Bind server selector. D-12 Phase 249: this page never pre-selected from a deep link before
    // this plan -- net-new here, mirroring feed.ts's/leaderboard.ts's identical read exactly.
    // Every `server[]` value is read, plus the legacy singular `server` value so an old bookmark
    // still pre-selects. The attribute-selector query below stays guarded against each untrusted
    // value; a value with no matching <option> is skipped entirely rather than echoed back.
    const selector = document.getElementById('mc-server-selector') as HTMLSelectElement | null;
    if (selector) {
        const params = new URLSearchParams(window.location.search);
        const deepLinkServers = params.getAll('server[]');
        const legacyServer = params.get('server');
        if (legacyServer) deepLinkServers.push(legacyServer);
        for (const deepLinkServer of deepLinkServers) {
            const option = selector.querySelector(`option[value="${CSS.escape(deepLinkServer)}"]`);
            if (option) (option as HTMLOptionElement).selected = true;
        }

        currentServers = Array.from(selector.selectedOptions).map((o) => o.value);

        selector.addEventListener('change', () => {
            currentServers = Array.from(selector.selectedOptions).map((o) => o.value);
            syncUrl();
            customFrom = undefined;
            customTo = undefined;
            fetchChartData();
            fetchHeatmapData();
            if (sessionsTable) sessionsTable.ajax.reload();
        });
    }

    // Bind time range buttons
    document.querySelectorAll<HTMLElement>('.mc-range-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
            const range = btn.dataset.range;
            if (!range) return;

            currentRange = range;
            customFrom = undefined;
            customTo = undefined;

            // Toggle active classes
            document.querySelectorAll('.mc-range-btn').forEach((b) => {
                b.classList.remove('btn-primary');
                b.classList.add('btn-outline-secondary');
            });
            btn.classList.remove('btn-outline-secondary');
            btn.classList.add('btn-primary');

            fetchChartData();
        });
    });

    // Bind custom date range via flatpickr
    const customInput = document.getElementById('mc-custom-range') as HTMLInputElement | null;
    if (customInput) {
        if (!(customInput as any)._flatpickr) { // idempotency guard (D-15, INPUT-01)
        flatpickr(customInput, {
            mode: 'range',
            dateFormat: 'Y-m-d',
            onChange: (selectedDates: Date[]) => {
                if (selectedDates.length === 2) {
                    customFrom = formatDate(selectedDates[0]);
                    customTo = formatDate(selectedDates[1]);
                    currentRange = 'custom';

                    // Clear active state from range buttons when using custom
                    document.querySelectorAll('.mc-range-btn').forEach((b) => {
                        b.classList.remove('btn-primary');
                        b.classList.add('btn-outline-secondary');
                    });

                    fetchChartData(customFrom, customTo);
                }
            },
        });
        } // end _flatpickr guard
    }

    // Init session DataTable
    initSessionsTable();

    // Initial load
    fetchChartData();
    fetchHeatmapData();
}

export function getCurrentServers(): string[] {
    return currentServers;
}

export function reloadSessions(): void {
    if (sessionsTable) sessionsTable.ajax.reload();
}

/**
 * D-09 Phase 249: one query-term builder shared by the chart fetch and the contribution-heatmap
 * fetch below -- a local equivalent of feed.ts's identical helper rather than a cross-module
 * import (these two controllers share no code today, and this phase is not the place to start).
 * Returns '' for the D-10 "all servers" state (an empty `currentServers`), or a
 * leading-ampersand-prefixed `server[]=` term per checked key.
 */
function serverQueryTerms(): string {
    return currentServers
        .map((v) => `&server[]=${encodeURIComponent(v)}`)
        .join('');
}

/**
 * D-11 Phase 249: net-new URL persistence -- this page never wrote its server selection back to
 * the address bar before this plan, mirroring feed.ts's/leaderboard.ts's identical `syncUrl()`.
 * Deletes BOTH the legacy scalar `server` key and the bracket `server[]` key before appending, so
 * a stale bookmarked scalar can never survive alongside the new list.
 *
 * The selection rides in the URL and NOWHERE else -- neither browser storage API is used
 * anywhere in this module, and nothing is written server-side. This repo has a documented bug
 * class where persisted dashboard query params let one visitor (or a probe) clobber another's
 * saved state; a read-only analytics page must not acquire it. The state-write call below is the
 * non-history-appending kind, so a viewer flipping through servers does not have to walk back
 * through every selection to leave the page.
 *
 * Not called from the range buttons or the custom date-range controls -- server selection only.
 */
function syncUrl(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete('server');
    url.searchParams.delete('server[]');
    for (const v of currentServers) {
        url.searchParams.append('server[]', v);
    }
    window.history.replaceState(null, '', url.toString());
}

// ---------------------------------------------------------------------------
// Avatar cell builders (D-04/D-06/D-07)
// ---------------------------------------------------------------------------

/**
 * D-04/D-06/Phase 249 D-15: a 24px player-head avatar tile plus the visible, truncating display
 * name, ported from allStats.ts's buildPlayerCell() (same mc-heads.net URL shape, loading="lazy",
 * empty alt, the visibility-hiding error listener; the 24px size itself comes from this plan's own
 * table-scoped CSS override, Task 2 below -- both this and the ported source request the `/32`
 * retina asset). Prefixed `Session` so a future reader cannot mistake this for a shared export of
 * allStats.ts's own builder.
 *
 * Unlike allStats.ts, `row.player_uuid` can be null here (D-04): a Session History row's
 * `player_name` may have no `mc_players` match. That state renders a NAME-keyed avatar with no
 * anchor at all, mirroring how buildServerCell()'s empty-`server_key` branch already degrades --
 * `/mc/players/{uuid}` needs a uuid it does not have, and Phase 239's D-14 rule is "only link when
 * the profile actually exists".
 */
function buildSessionPlayerCell(row: SessionIdentityRow): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'mc-board-player';

    // Test for a non-empty STRING, not truthiness alone, so a server-side '' regression renders
    // the non-clickable fallback below rather than a broken `/mc/players/` href (T-256-09-04).
    if (typeof row.player_uuid === 'string' && row.player_uuid !== '') {
        const uuid = row.player_uuid;

        const link = document.createElement('a');
        // D-06: the BRACKET form, not a legacy scalar -- the profile page reads a list, and every
        // session row knows exactly one owning server. An empty key omits the suffix entirely
        // rather than emitting a scope that reads as "scoped to nothing".
        const scopeSuffix = row.server_key !== ''
            ? `?server[]=${encodeURIComponent(row.server_key)}`
            : '';
        link.href = `/mc/players/${encodeURIComponent(uuid)}${scopeSuffix}`;
        link.title = row.player;

        const tile = document.createElement('span');
        tile.className = 'mc-avatar-tile';
        const img = document.createElement('img');
        img.src = `https://mc-heads.net/avatar/${encodeURIComponent(uuid)}/32`;
        img.loading = 'lazy';
        img.width = 24;
        img.height = 24;
        img.alt = '';
        img.className = 'mc-player-avatar';
        // Visibility, not display -- the placeholder background shows through with zero layout
        // shift. Guaranteed rendering for a non-Mojang uuid (mc-heads.net always 404s for one),
        // not a rare fallback.
        img.addEventListener('error', () => { img.style.visibility = 'hidden'; });
        tile.appendChild(img);
        link.appendChild(tile);

        const name = document.createElement('span');
        name.className = 'mc-board-name';
        name.textContent = row.player;
        link.appendChild(name);

        wrap.appendChild(link);
        return wrap;
    }

    // D-04 unresolved branch: no anchor at all -- `/mc/players/{uuid}` needs a uuid this row does
    // not have (Phase 239 D-14: only link when the profile actually exists). The tile's <img> is
    // NAME-keyed rather than uuid-keyed and carries NO error listener: RESEARCH.md curled both
    // mc-heads.net endpoints live and found the uuid path 404s for a non-Mojang uuid (which is why
    // the resolved branch above keeps its listener), while the name path returns HTTP 200 with a
    // default skin for a name that does not exist -- a listener here would never fire.
    const tile = document.createElement('span');
    tile.className = 'mc-avatar-tile';
    const img = document.createElement('img');
    img.src = `https://mc-heads.net/avatar/${encodeURIComponent(row.player)}/32`;
    img.loading = 'lazy';
    img.width = 24;
    img.height = 24;
    img.alt = '';
    img.className = 'mc-player-avatar';
    tile.appendChild(img);
    wrap.appendChild(tile);

    const name = document.createElement('span');
    name.className = 'mc-board-name mc-board-name--unresolved';
    name.textContent = row.player;
    wrap.appendChild(name);

    wrap.title = 'This session’s player name has no matching player profile yet -- not clickable.';

    return wrap;
}

/**
 * D-07: a 24px server-icon avatar tile plus the visible, truncating display name, a direct port of
 * allStats.ts's buildServerCell() with no behavioural change. `server_icon` is used directly as
 * `img.src` -- already sanitized server-side via `FaviconUrl::sanitize()` (Plan 256-06, Phase 249's
 * T-249-I-01 decision) -- with no client-side re-validation added. `/mc/{key}` is the Mc:detail
 * route mask: an icon always takes you to that server, never a same-page filter narrow.
 */
function buildSessionServerCell(row: SessionIdentityRow): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'mc-board-player';

    const tile = document.createElement('span');
    tile.className = 'mc-server-icon-tile';
    if (row.server_icon !== null) {
        const img = document.createElement('img');
        img.src = row.server_icon;
        img.loading = 'lazy';
        img.width = 24;
        img.height = 24;
        img.alt = '';
        img.addEventListener('error', () => { img.style.visibility = 'hidden'; });
        tile.appendChild(img);
    }

    const name = document.createElement('span');
    name.className = 'mc-board-name';
    name.textContent = row.server;

    if (row.server_key === '') {
        wrap.appendChild(tile);
        wrap.appendChild(name);
        return wrap;
    }

    const link = document.createElement('a');
    link.href = `/mc/${encodeURIComponent(row.server_key)}`;
    link.title = row.server;
    link.appendChild(tile);
    link.appendChild(name);
    wrap.appendChild(link);
    return wrap;
}

// ---------------------------------------------------------------------------
// DataTable initialization
// ---------------------------------------------------------------------------

function initSessionsTable(): void {
    const tableEl = document.querySelector('.datatable[data-table="mc-sessions"]');
    if (!tableEl) return;

    sessionsTable = new DataTable('.datatable[data-table="mc-sessions"]', {
        pageLength: 25,
        processing: true,
        serverSide: true,
        ajax: {
            url: '?do=getSessions',
            data: function (data: any) {
                // D-12 Phase 249: assign the ARRAY directly -- jQuery's default parameter
                // serialization turns an array-valued key into repeated `server[]` terms with no
                // manual encoding, which is exactly the wire format McPresenter::readServerKeys()
                // reads. Never pre-join this into a string.
                data.server = currentServers;
                data.player = getPlayerFilter();
                data.dateFrom = getDateFrom();
                data.dateTo = getDateTo();
                return data;
            },
        },
        columns: [
            {
                data: 'player',
                name: 'player',
                render: DataTable.render.text(),
                createdCell: (cell: HTMLTableCellElement, _cellData: unknown, rowData: SessionIdentityRow) => {
                    cell.textContent = '';
                    cell.appendChild(buildSessionPlayerCell(rowData));
                },
            },
            {
                data: 'server',
                name: 'server',
                render: DataTable.render.text(),
                createdCell: (cell: HTMLTableCellElement, _cellData: unknown, rowData: SessionIdentityRow) => {
                    cell.textContent = '';
                    cell.appendChild(buildSessionServerCell(rowData));
                },
            },
            { data: 'joined' },
            {
                data: 'left',
                render: function (data: any) {
                    if (data === null) {
                        return '<span class="mc-online-badge"><span class="mc-online-dot"></span> Online now</span>';
                    }
                    return data;
                },
            },
            { data: 'duration' },
        ],
        order: [[2, 'desc']],
    });

    // Bind player name filter with debounce
    const playerFilter = document.getElementById('mc-player-filter') as HTMLInputElement | null;
    if (playerFilter) {
        let debounceTimer: ReturnType<typeof setTimeout>;
        playerFilter.addEventListener('keyup', () => {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
                if (sessionsTable) sessionsTable.ajax.reload();
            }, 300);
        });
    }

    // Bind date range filters via flatpickr
    const dateFromInput = document.getElementById('mc-sessions-date-from') as HTMLInputElement | null;
    const dateToInput = document.getElementById('mc-sessions-date-to') as HTMLInputElement | null;

    if (dateFromInput) {
        if (!(dateFromInput as any)._flatpickr) { // idempotency guard (D-15, INPUT-01)
            flatpickr(dateFromInput, {
                dateFormat: 'Y-m-d',
                onChange: () => {
                    if (sessionsTable) sessionsTable.ajax.reload();
                },
            });
        }
    }

    if (dateToInput) {
        if (!(dateToInput as any)._flatpickr) { // idempotency guard (D-15, INPUT-01)
            flatpickr(dateToInput, {
                dateFormat: 'Y-m-d',
                onChange: () => {
                    if (sessionsTable) sessionsTable.ajax.reload();
                },
            });
        }
    }
}

function getPlayerFilter(): string {
    const el = document.getElementById('mc-player-filter') as HTMLInputElement | null;
    return el ? el.value : '';
}

function getDateFrom(): string {
    const el = document.getElementById('mc-sessions-date-from') as HTMLInputElement | null;
    return el ? el.value : '';
}

function getDateTo(): string {
    const el = document.getElementById('mc-sessions-date-to') as HTMLInputElement | null;
    return el ? el.value : '';
}

// ---------------------------------------------------------------------------
// Heatmap
// ---------------------------------------------------------------------------

export async function fetchHeatmapData(): Promise<void> {
    const url = `?do=getHeatmap${serverQueryTerms()}`;

    try {
        const resp = await fetch(url, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });

        if (!resp.ok) {
            console.error('[mc-analytics] Heatmap fetch failed:', resp.status);
            return;
        }

        const data: HeatmapPayload = await resp.json();
        renderHeatmap(data.days);
    } catch (err) {
        console.error('[mc-analytics] Heatmap fetch error:', err);
    }
}

function renderHeatmap(days: Record<string, HeatmapDay>): void {
    const container = document.getElementById('mc-analytics-heatmap');
    if (!container) return;

    // Clear existing content
    container.innerHTML = '';

    // Calculate max hours for color scaling
    const hourValues = Object.values(days).map((d) => d.hours);
    const maxHours = hourValues.length > 0 ? Math.max(...hourValues) : 1;

    // Calculate start date: 52 weeks ago, aligned to Sunday
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(today);
    startDate.setDate(startDate.getDate() - 52 * 7);
    // Align to Sunday (day 0)
    const startDow = startDate.getDay(); // 0=Sun
    startDate.setDate(startDate.getDate() - startDow);

    const totalWeeks = 53;

    // Phase 181: isDark reader removed (Pitfall 6 — the prior code read from document.body which is
    // the wrong element; Phase 178 sets data-bs-theme on <html> not <body>). STATE.md single-reader
    // constraint restored: only getChartTheme() may read the theme. Empty/future heatmap cells use
    // var(--ksat-surface-raised) — resolves live at paint from [data-bs-theme] cascade, no JS branching.

    // Outer wrapper for overflow on mobile
    const wrapper = document.createElement('div');
    wrapper.style.overflowX = 'auto';

    // Build the grid layout: day-labels col + 53 week cols
    const grid = document.createElement('div');
    grid.className = 'mc-heatmap-grid';

    // Day labels column (Mon, Wed, Fri)
    const dayLabelsCol = document.createElement('div');
    dayLabelsCol.style.display = 'flex';
    dayLabelsCol.style.flexDirection = 'column';
    dayLabelsCol.style.gap = '2px';
    dayLabelsCol.style.paddingTop = '18px'; // leave room for month labels

    const dayNames = ['', 'Mon', '', 'Wed', '', 'Fri', ''];
    for (const name of dayNames) {
        const label = document.createElement('div');
        label.className = 'mc-heatmap-label';
        label.style.height = '12px';
        label.style.lineHeight = '12px';
        label.textContent = name;
        dayLabelsCol.appendChild(label);
    }
    grid.appendChild(dayLabelsCol);

    // Track month label positions
    let lastMonth = -1;

    for (let week = 0; week < totalWeeks; week++) {
        const weekCol = document.createElement('div');
        weekCol.style.display = 'flex';
        weekCol.style.flexDirection = 'column';
        weekCol.style.gap = '2px';

        // Month label
        const monthLabel = document.createElement('div');
        monthLabel.className = 'mc-heatmap-label';
        monthLabel.style.height = '16px';
        monthLabel.style.lineHeight = '16px';
        monthLabel.style.marginBottom = '2px';

        const weekStartDate = new Date(startDate);
        weekStartDate.setDate(weekStartDate.getDate() + week * 7);
        const month = weekStartDate.getMonth();
        if (month !== lastMonth) {
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            monthLabel.textContent = monthNames[month];
            lastMonth = month;
        }
        weekCol.appendChild(monthLabel);

        // Day cells for this week
        for (let dow = 0; dow < 7; dow++) {
            const cellDate = new Date(startDate);
            cellDate.setDate(cellDate.getDate() + week * 7 + dow);

            const cell = document.createElement('div');
            cell.className = 'mc-heatmap-cell';

            const isFuture = cellDate > today;
            if (isFuture) {
                cell.classList.add('mc-heatmap-future');
                // var(--ksat-surface-raised) resolves live at paint — no JS theme branch needed
                cell.style.backgroundColor = 'var(--ksat-surface-raised)';
            } else {
                const dateStr = formatDate(cellDate);
                const dayData = days[dateStr];
                const hours = dayData ? dayData.hours : 0;
                cell.style.backgroundColor = intensityToGreen(hours, maxHours);

                const tooltipTitle = hours > 0
                    ? `${dateStr}: ${hours.toFixed(1)} hrs${dayData.players ? ' -- ' + dayData.players : ''}`
                    : `${dateStr}: No activity`;
                cell.dataset.bsToggle = 'tooltip';
                cell.dataset.bsTitle = tooltipTitle;
            }

            weekCol.appendChild(cell);
        }

        grid.appendChild(weekCol);
    }

    wrapper.appendChild(grid);
    container.appendChild(wrapper);

    // Color legend
    const legend = document.createElement('div');
    legend.className = 'mc-heatmap-legend';
    // Use var(--ksat-surface-raised) for the "empty" legend swatch — resolves live at paint
    legend.innerHTML = `
        <span class="mc-heatmap-label">Less</span>
        <div class="mc-heatmap-cell" style="background-color: var(--ksat-surface-raised)"></div>
        <div class="mc-heatmap-cell" style="background-color: #9be9a8"></div>
        <div class="mc-heatmap-cell" style="background-color: #40c463"></div>
        <div class="mc-heatmap-cell" style="background-color: #30a14e"></div>
        <div class="mc-heatmap-cell" style="background-color: #216e39"></div>
        <span class="mc-heatmap-label">More</span>
    `;
    container.appendChild(legend);

    // Initialize Bootstrap tooltips on heatmap cells
    init_tooltips();
}

// Phase 181: isDark parameter removed — 0-hours case now handled at call site with var(--ksat-surface-raised).
// Only non-zero intensity colors remain here (green palette = data identity, not chrome, D-01 — kept fixed).
function intensityToGreen(hours: number, maxHours: number): string {
    if (hours === 0) {
        // Caller should use 'var(--ksat-surface-raised)' for empty cells; this path is a fallback safety.
        return 'var(--ksat-surface-raised)';
    }

    const ratio = hours / Math.max(maxHours, 0.001);

    if (ratio < 0.25) return '#9be9a8';
    if (ratio < 0.50) return '#40c463';
    if (ratio < 0.75) return '#30a14e';
    return '#216e39';
}

// ---------------------------------------------------------------------------
// Data fetching (chart)
// ---------------------------------------------------------------------------

async function fetchChartData(from?: string, to?: string): Promise<void> {
    const loadingEl = document.getElementById('mc-chart-loading');
    const emptyEl = document.getElementById('mc-chart-empty');
    const chartEl = document.getElementById('mc-analytics-chart');

    if (loadingEl) loadingEl.style.display = 'block';
    if (emptyEl) emptyEl.classList.add('d-none');

    let url = `?do=getAnalyticsChart&range=${encodeURIComponent(currentRange)}${serverQueryTerms()}`;
    if (from && to) {
        url += `&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
    }

    try {
        const resp = await fetch(url, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });

        if (!resp.ok) {
            console.error('[mc-analytics] Chart fetch failed:', resp.status);
            if (loadingEl) loadingEl.style.display = 'none';
            return;
        }

        const data: ChartPayload = await resp.json();

        if (loadingEl) loadingEl.style.display = 'none';

        // Check for empty data
        const hasData = data.series.some((s) => s.points.length > 0);
        if (!hasData) {
            if (chartEl) chartEl.style.display = 'none';
            if (emptyEl) emptyEl.classList.remove('d-none');
            return;
        }

        if (chartEl) chartEl.style.display = '';
        if (emptyEl) emptyEl.classList.add('d-none');

        renderChart(data);
    } catch (err) {
        console.error('[mc-analytics] Chart fetch error:', err);
        if (loadingEl) loadingEl.style.display = 'none';
    }
}

// ---------------------------------------------------------------------------
// Chart rendering
// ---------------------------------------------------------------------------

function renderChart(data: ChartPayload): void {
    if (chartInstance) {
        chartInstance.destroy();
        chartInstance = null;
    }

    const series = data.series.map((s, i) => ({
        type: 'area' as const,
        name: s.name,
        data: s.points,
        color: SERVER_COLORS[i % SERVER_COLORS.length],
    }));

    // Phase 181: getChartTheme() supplies chrome (background, axis, tooltip surface).
    // backgroundColor: 'transparent' keeps the card token visible through the chart.
    // No data-bs-theme reader here — only getChartTheme() reads the theme (STATE.md constraint).
    // The global browser-local time-rendering flag is set once in src/index.ts — no per-chart
    // override needed; deliberately omitted here.
    chartInstance = Highcharts.chart('mc-analytics-chart', Highcharts.merge(getChartTheme(), {
        chart: {
            type: 'area',
            backgroundColor: 'transparent',
        },
        title: {
            text: '',
        },
        plotOptions: {
            area: {
                stacking: data.stacked ? 'normal' : undefined,
                marker: { enabled: false },
                lineWidth: 1,
                fillOpacity: 0.3,
            },
        },
        xAxis: {
            type: 'datetime',
        },
        yAxis: {
            min: 0,
            allowDecimals: false,
            title: { text: 'Players' },
        },
        // Phase 240 (240-06, D-05/D-07/D-08/SC2/SC5): the tooltip now leads with
        // chartInstantHeader() — the exact browser-timezone instant plus its relative age — above
        // per-series rows that hand-replicate Highcharts' own default shared-tooltip row rendering
        // for a line/area series. The old built-in date-format tooltip option is dropped: a custom
        // `formatter` supersedes it, and leaving a dead option in place would invite a future
        // reader to think it still drives the header.
        //
        // Deviation #2 (phase-stated, 240-RESEARCH.md's correction to CONTEXT's D-07 text): the
        // formatter returns a single STRING, not an array. The array-return contract belongs to
        // `tooltip.split:true`; this chart is `shared:true`, and every existing formatter in this
        // repo returns a string.
        //
        // CHRT-03 scoping: chartTheme.ts:4-8 binds getChartTheme()'s OWN return value only. This
        // formatter lives in the per-chart second argument to Highcharts.merge(), which is
        // unaffected (in-repo precedent: transactions.ts:1053, and resourceHistory.ts as of 240-05).
        //
        // D-08 provenance — do NOT convert `this.x`. This chart's payload is
        // `s.points: [epochMs, value][]`: the conversion happens SERVER-side, in
        // McAnalyticsModel::getChartData() (app/Models/McAnalyticsModel.php:74,
        // `$ts = strtotime($row->bucket) * 1000;`), and the already-epoch-ms number ships over
        // JSON. By the time a point reaches this formatter `this.x` is already a correct epoch-ms
        // number — pass it straight to the header helper. No re-parse (there is no string at this
        // layer), no manual offset "to be safe", and no per-chart browser-vs-UTC rendering-mode
        // override — that flag is GLOBAL (src/index.ts:56-61), load-bearing, and a standing repo
        // convention with its own project-memory entry.
        //
        // ⚠ PRE-EXISTING AND OUT OF THIS PHASE'S FIX SCOPE (240-RESEARCH.md assumption A2): that
        // server-side strtotime() call carries no explicit ' UTC' suffix, so its correctness
        // depends on PHP's runtime default timezone being UTC — no date_default_timezone_set()
        // call exists in app/Bootstrap.php or config/*.neon, and the deployed php.ini
        // date.timezone value could not be verified from the planning sandbox. This formatter adds
        // NO new risk (it reads the already-computed epoch and touches that conversion not at
        // all), but this is the one x-value source in the phase's scope that does not use the
        // explicit-' UTC' idiom that parseUtcTimestamp() and McFeedModel::occurredAtEpochMs() both
        // use. Recorded here deliberately rather than silently inherited — or wrongly "fixed" from
        // inside a tooltip.
        tooltip: {
            shared: true,
            formatter: function (this: Highcharts.Point): string {
                const header = chartInstantHeader(this.x as number);
                // SC5: iterate this.points in the order Highcharts hands them over — no filtering
                // and no reordering. makeEmptyAwareTooltip()'s shared branch drops null/<=0 points
                // and reorders by value descending; copying that loop here would hide legitimate
                // zero-player buckets (an empty server is real data) and scramble the stacked
                // per-server row order against the legend.
                const rows = (this.points ?? []).map((p) => {
                    // T-240-21/T-240-17: `style="color:…"` is the row's ONLY attribute context, and
                    // it is guarded to a real CSS color string so a gradient/pattern object can
                    // never serialize into the attribute. The series name (a server display name
                    // from mc_servers.name — operator-configured, never player- or wire-supplied)
                    // stays in a TEXT position, rendered through the exact same Highcharts tooltip
                    // path the built-in default pointFormat already used for it.
                    const color = typeof p.series.color === 'string' ? p.series.color : '#aaa';
                    // T-240-22: a formatter that throws breaks every tooltip on the page, so every
                    // access is guarded and a null y renders an empty bold value rather than
                    // dropping the row — matching the default renderer's own null-point behavior.
                    // numberFormat(…, 0) matches this chart's own yAxis allowDecimals:false; no
                    // valueSuffix is added because no series defines one today.
                    const valueHtml = p.y == null
                        ? '<b></b>'
                        : `<b>${Highcharts.numberFormat(p.y, 0)}</b>`;
                    return `<span style="color:${color}">●</span> ${p.series.name}: ${valueHtml}`;
                }).join('<br/>');
                return header + '<br/>' + rows;
            } as Highcharts.TooltipFormatterCallbackFunction,
        },
        legend: {
            enabled: data.stacked,
        },
        series,
        credits: { enabled: false },
    }));
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}
